import { createFileRoute, Link } from "@tanstack/react-router"
import { Container, Title, Text, Paper, Stack, Grid, Box, List, Badge, Button } from "@mantine/core"
import {
  IconGavel,
  IconEye,
  IconShield,
  IconUsers,
  IconFileCheck,
  IconArrowRight,
  IconExternalLink,
  IconShieldCheck,
} from "@tabler/icons-react"

export const Route = createFileRoute("/about")({
  component: AboutComponent,
})

function AboutComponent() {
  const values = [
    {
      icon: IconEye,
      title: "Transparency",
      description:
        "All information should be accessible to the public when it serves the interest of justice and accountability.",
    },
    {
      icon: IconShield,
      title: "Accuracy",
      description: "We verify sources, fact-check information, and clearly distinguish between facts and analysis.",
    },
    {
      icon: IconGavel,
      title: "Justice",
      description: "Truth and accountability are essential for a functioning democracy and justice system.",
    },
    {
      icon: IconUsers,
      title: "Public Service",
      description:
        "We serve the public interest by organizing and presenting information that is already in the public domain.",
    },
  ]

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Box ta="center" mb="xl">
          <Box
            style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
              borderRadius: "16px",
              padding: "12px",
              boxShadow: "0 8px 24px rgba(59, 130, 246, 0.3)",
              display: "inline-flex",
              marginBottom: "24px",
            }}
          >
            <IconGavel size={32} color="white" />
          </Box>
          <Title order={1} size="h1" mb="md">
            About RTEFN
          </Title>
          <Text size="xl" c="dimmed" maw={600} mx="auto">
            Release The Epstein Files Now
          </Text>
        </Box>

        {/* Who We Are */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl" style={{ borderLeft: "4px solid #3b82f6" }}>
          <Stack gap="lg">
            <Title order={2} size="h2" mb="md">
              Who We Are
            </Title>
            <Text size="lg" lh={1.6}>
              RTEFN is an independent research and transparency advocacy project created and maintained by an individual
              researcher committed to accountability in the Epstein case. This project operates without political
              affiliation, organizational funding, or institutional backing.
            </Text>
            <Text size="lg" lh={1.6}>
              <strong>Why pseudonymous?</strong> This project focuses on facts, methodology, and transparency—not
              personality or personal recognition. By maintaining researcher anonymity, the emphasis remains on the
              quality of verification and the strength of evidence rather than who presents it.
            </Text>
            <Text size="lg" lh={1.6}>
              <strong>Independence:</strong> No funding from any organization, political party, or special interest
              group. No affiliation with any media organization or legal entity. Content decisions are made solely based
              on public interest and verification standards.
            </Text>
          </Stack>
        </Paper>

        {/* Mission Statement */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h2" mb="md">
              Our Mission
            </Title>
            <Text size="lg" lh={1.6}>
              RTEFN advocates for the full public release of Jeffrey Epstein case files and related documents in the
              interest of transparency, justice, and public accountability. We believe that justice requires
              transparency, and the public has a right to access information about cases that involve questions of
              power, influence, and potential systemic failures.
            </Text>
            <Text size="lg" lh={1.6}>
              By organizing publicly available court documents, depositions, and verified information, we ensure that
              critical details about this case remain accessible to journalists, researchers, and citizens who seek to
              understand what happened and hold institutions accountable.
            </Text>
          </Stack>
        </Paper>

        {/* What We Do */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              What We Do
            </Title>
            <List spacing="md" size="md">
              <List.Item>
                <strong>Document Organization:</strong> We collect, verify, and organize publicly available court
                documents, legal filings, and depositions related to the Jeffrey Epstein case.
              </List.Item>
              <List.Item>
                <strong>Timeline Maintenance:</strong> We maintain a comprehensive timeline of key events, court
                decisions, and document releases to provide context and chronology.
              </List.Item>
              <List.Item>
                <strong>Source Verification:</strong> All documents are verified for authenticity and properly sourced
                to official court records or credible news outlets.
              </List.Item>
              <List.Item>
                <strong>Public Access:</strong> We present information in an accessible format that serves researchers,
                journalists, and concerned citizens.
              </List.Item>
            </List>
          </Stack>
        </Paper>

        {/* How We Verify */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl" style={{ borderLeft: "4px solid #10b981" }}>
          <Stack gap="lg">
            <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <IconFileCheck size={28} color="#10b981" />
              <Title order={2} size="h3">
                How We Verify Information
              </Title>
            </Box>
            <Text size="md" lh={1.6}>
              Every piece of content on RTEFN follows a rigorous verification process aligned with the International
              Fact-Checking Network (IFCN) Code of Principles:
            </Text>
            <List spacing="md" size="md">
              <List.Item>
                <strong>4-Tier Source Hierarchy:</strong> All sources are classified by credibility—from Tier 1 (court
                documents, official records) to Tier 4 (unverified rumors). Verified content requires Tier 1-2 sources.
              </List.Item>
              <List.Item>
                <strong>Multiple Source Verification:</strong> Significant claims require confirmation from 2-4
                independent sources depending on the tier and impact.
              </List.Item>
              <List.Item>
                <strong>Direct Source Authentication:</strong> Documents are verified through official databases like
                PACER, CourtListener, FBI Vault, and other government archives.
              </List.Item>
              <List.Item>
                <strong>Cross-Reference Validation:</strong> Information is checked for consistency across multiple
                credible sources. Discrepancies are investigated and noted.
              </List.Item>
              <List.Item>
                <strong>Legal & Ethical Review:</strong> All content undergoes compliance review to protect privacy,
                respect victims, and ensure legal compliance.
              </List.Item>
            </List>
            <Box>
              <Button
                component={Link}
                to="/methodology"
                variant="light"
                color="green"
                rightSection={<IconArrowRight size={16} />}
                size="md"
              >
                View Complete Methodology
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* Why Trust Us */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl" style={{ backgroundColor: "var(--mantine-color-blue-0)" }}>
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              Why Trust This Site?
            </Title>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Stack gap="sm">
                  <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <IconExternalLink size={20} color="#3b82f6" />
                    <Text size="md" fw={600}>
                      Direct Source Links
                    </Text>
                  </Box>
                  <Text size="sm" c="dimmed">
                    Every document includes direct links to PACER, FBI Vault, CourtListener, or other official sources.
                    Verify our work yourself.
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Stack gap="sm">
                  <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <IconShieldCheck size={20} color="#3b82f6" />
                    <Text size="md" fw={600}>
                      Visible Verification Status
                    </Text>
                  </Box>
                  <Text size="sm" c="dimmed">
                    All content is clearly marked as "Verified," "Partially Verified," or "Unverified" with explanations
                    of how we determined the status.
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Stack gap="sm">
                  <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <IconFileCheck size={20} color="#3b82f6" />
                    <Text size="md" fw={600}>
                      Open Methodology
                    </Text>
                  </Box>
                  <Text size="sm" c="dimmed">
                    Our complete verification process is public and follows IFCN standards. Scrutinize our methods—we
                    welcome it.
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Stack gap="sm">
                  <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <IconEye size={20} color="#3b82f6" />
                    <Text size="md" fw={600}>
                      Transparent Corrections
                    </Text>
                  </Box>
                  <Text size="sm" c="dimmed">
                    Errors are corrected immediately with clear explanations. No silent edits—all changes are documented
                    and explained.
                  </Text>
                </Stack>
              </Grid.Col>
            </Grid>
            <Box mt="md">
              <Badge variant="light" color="blue" size="lg" px="xl" py="sm">
                IFCN-Aligned Standards • Independent Verification • Complete Transparency
              </Badge>
            </Box>
          </Stack>
        </Paper>

        {/* What We Don't Do */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              What We Don't Do
            </Title>
            <List spacing="md" size="md">
              <List.Item>
                We do not create conspiracy theories or promote unverified claims about individuals or events.
              </List.Item>
              <List.Item>
                We do not publish private information, personal details, or content that could harm victims or
                witnesses.
              </List.Item>
              <List.Item>
                We do not advocate for specific legal outcomes or political positions beyond transparency and
                accountability.
              </List.Item>
              <List.Item>
                We do not host or distribute illegal content or information obtained through unauthorized means.
              </List.Item>
            </List>
          </Stack>
        </Paper>

        {/* Our Values */}
        <Stack gap="xl">
          <Title order={2} size="h2" ta="center">
            Our Values
          </Title>
          <Grid>
            {values.map((value, index) => (
              <Grid.Col key={index} span={{ base: 12, sm: 6 }}>
                <Paper shadow="sm" radius="md" p="lg" h="100%">
                  <Stack gap="md">
                    <Box
                      style={{
                        background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                        borderRadius: "12px",
                        padding: "8px",
                        boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                        display: "inline-flex",
                        alignSelf: "flex-start",
                      }}
                    >
                      <value.icon size={20} color="white" />
                    </Box>
                    <Title order={3} size="h4">
                      {value.title}
                    </Title>
                    <Text c="dimmed" lh={1.5}>
                      {value.description}
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        </Stack>

        {/* Why This Matters */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              Why This Matters
            </Title>
            <Text size="md" lh={1.6}>
              The Jeffrey Epstein case involves allegations of serious crimes, questions about institutional failures,
              and concerns about how wealth and influence may have affected the pursuit of justice. Public access to
              court documents and verified information is essential for:
            </Text>
            <List spacing="md" size="md" mt="md">
              <List.Item>
                <strong>Accountability:</strong> Ensuring that all individuals and institutions involved are held
                responsible for their actions.
              </List.Item>
              <List.Item>
                <strong>Justice for Victims:</strong> Supporting the rights of victims to have their cases fully
                investigated and prosecuted.
              </List.Item>
              <List.Item>
                <strong>Systemic Reform:</strong> Identifying and addressing any systemic issues that may have enabled
                these alleged crimes.
              </List.Item>
              <List.Item>
                <strong>Public Trust:</strong> Maintaining confidence in our justice system through transparency and
                proper oversight.
              </List.Item>
            </List>
          </Stack>
        </Paper>

        {/* Legal and Ethical Framework */}
        <Paper shadow="sm" radius="md" p="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              Legal and Ethical Framework
            </Title>
            <Text size="md" lh={1.6}>
              RTEFN operates within strict legal and ethical guidelines:
            </Text>
            <List spacing="md" size="md" mt="md">
              <List.Item>All content is based on publicly available information and court records.</List.Item>
              <List.Item>
                We respect the privacy rights of individuals not central to the public interest aspects of the case.
              </List.Item>
              <List.Item>We maintain the highest standards of accuracy and source verification.</List.Item>
              <List.Item>
                We comply with all applicable laws regarding the publication of legal documents and news content.
              </List.Item>
              <List.Item>We are committed to correcting any errors promptly and transparently.</List.Item>
            </List>
            <Badge variant="light" color="blue" size="lg" mt="md" px="xl" py="sm">
              Transparency • Justice • Accountability
            </Badge>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  )
}
